import React, { Component } from 'react';
import { Tabs, Switch } from 'antd';
import _EnterpriseOpsService from 'modules/EnterpriseOpsService';
import _notification from 'modules/notification';
import MyInput from 'components/controls/MyInput';
import Button from 'components/controls/Button';
import PageHeader from 'components/enterprise/PageHeader';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import EmptyState from 'components/enterprise/EmptyState';
import StatusBadge from 'components/enterprise/StatusBadge';
import StatCard from 'components/enterprise/StatCard';
import SecretRefField from 'components/controls/SecretRefField';
import { routesAdmin } from 'routes/Routes';

// Sprint 2.6.5 (Enterprise Identity Configuration): describes every identity-verification mechanism the
// Identity tab presents, driving the Enabled/Disabled/Configured/Not Configured/Future Capability
// indicator per Part 1 of the sprint brief. `flagKey` = null means no EnterpriseConfiguration feature flag
// exists for this mechanism at all (a pure future placeholder, per the Phase A discovery). `healthComponent`
// = null means no vendor-config health check applies (nothing to be "configured" against yet).
const IDENTITY_MECHANISMS = [
    { key: 'MobileOtp', label: 'Mobile OTP', flagKey: 'MobileOtpVerification', healthComponent: 'Mobile OTP (MSG91)' },
    { key: 'EmailOtp', label: 'Email OTP', flagKey: 'EmailOtpVerification', healthComponent: 'Email (SendGrid)' },
    { key: 'Aadhaar', label: 'Aadhaar Verification', flagKey: 'AadhaarVerification', healthComponent: 'Aadhaar Verification (Surepass)' },
    { key: 'Biometric', label: 'Biometric Verification', flagKey: 'BiometricVerification', healthComponent: null, isFuture: true },
    { key: 'DigiLocker', label: 'DigiLocker', flagKey: null, healthComponent: null, isFuture: true }
];

// Sprint 2.7.1 (Enterprise Communication Foundation) — Part 4: the 2 channels
// CommunicationHealthService.GetHealth() reports on today. `healthKey` matches the property name on the
// CommunicationHealthModel response (Email/Sms).
const COMMUNICATION_CHANNELS = [
    { key: 'Email', label: 'Email', healthKey: 'Email', testPlaceholder: 'candidate@example.com', testLabel: 'Send Test Email' },
    { key: 'Sms', label: 'SMS', healthKey: 'Sms', testPlaceholder: '10-digit mobile number', testLabel: 'Send Test SMS' }
];

// Sprint 2.7.2A (Enterprise Communication Experience Modernization): every "Communication" category
// ConfigKey, mapped to an administrator-friendly label/help text/input type. Nothing here changes what is
// stored, how it is saved, or how it is used at runtime — this is the "translation layer" between the raw
// ConfigKey names (Email.Smtp.PasswordSecretRef) and what an Examination Administrator actually reads
// (SMTP Password). See ECMF_Information_Architecture.md for the full reasoning behind every grouping
// decision below.
const COMMUNICATION_FIELD_GROUPS = [
    {
        key: 'email',
        icon: '📧',
        title: 'Email Communication',
        description: 'Configure how emails are sent from the Registration Portal.',
        sections: [
            {
                subtitle: 'Email Service Provider',
                fields: [
                    { key: 'Email.Provider', label: 'Email Service Provider', type: 'select', options: ['SendGrid', 'Smtp', 'AzureCommunicationServices', 'AmazonSES'], optionLabels: { SendGrid: 'SendGrid (active today)', Smtp: 'SMTP (not yet available)', AzureCommunicationServices: 'Azure Communication Services (coming soon)', AmazonSES: 'Amazon SES (coming soon)' }, help: 'Which service actually delivers your emails today.' }
                ]
            },
            {
                subtitle: 'Sender Information',
                fields: [
                    { key: 'Email.SenderName', label: 'Sender Name', type: 'text', help: 'The name candidates see in their inbox — e.g. "National Bank Recruitment".' },
                    { key: 'Email.SenderEmail', label: 'Sender Email Address', type: 'email', full: true, help: 'The address candidates’ emails appear to come from.' },
                    { key: 'Email.ReplyToEmail', label: 'Reply-To Email Address', type: 'email', optional: true, full: true, help: 'Optional. Where a candidate’s reply should go, if different from the sender address.' }
                ]
            },
            {
                subtitle: 'SMTP Configuration',
                note: 'Only used when Email Service Provider (above) is set to SMTP.',
                fields: [
                    { key: 'Email.Smtp.Host', label: 'SMTP Server', type: 'text', full: true, help: 'Provided by your email hosting/IT team, e.g. smtp.yourbank.com.' },
                    { key: 'Email.Smtp.Username', label: 'SMTP Username', type: 'text', full: true },
                    { key: 'Email.Smtp.PasswordSecretRef', label: 'SMTP Password', type: 'secretRef', full: true, help: 'For security, the password itself is never stored here. Enter the name of the secure setting your IT team configured to hold it.' }
                ]
            },
            {
                subtitle: 'Connection Settings',
                note: 'Only used when Email Service Provider (above) is set to SMTP.',
                fields: [
                    { key: 'Email.Smtp.Port', label: 'SMTP Port', type: 'number', help: 'Must be numeric — commonly 587 (recommended) or 465.' },
                    { key: 'Email.Smtp.Encryption', label: 'Connection Security', type: 'select', options: ['None', 'StartTls', 'Ssl'], optionLabels: { None: 'None', StartTls: 'STARTTLS (recommended)', Ssl: 'SSL' } }
                ]
            }
        ]
    },
    {
        key: 'sms',
        icon: '📱',
        title: 'SMS Communication',
        description: 'Configure your SMS provider so candidates receive text-message updates.',
        sections: [
            {
                subtitle: 'SMS Service Provider',
                fields: [
                    { key: 'Sms.Provider', label: 'SMS Service Provider', type: 'select', options: ['MSG91', 'AzureCommunicationServices', 'Twilio'], optionLabels: { MSG91: 'MSG91 (active for OTP)', AzureCommunicationServices: 'Azure Communication Services (coming soon)', Twilio: 'Twilio (coming soon)' }, help: 'Which vendor delivers OTP text messages today.' }
                ]
            },
            {
                subtitle: 'Authentication',
                fields: [
                    { key: 'Sms.Msg91.AuthKeySecretRef', label: 'MSG91 Authentication Key', type: 'secretRef', full: true, help: 'These settings are typically provided by your SMS vendor. For security, the key itself is never stored here — enter the name of the secure setting your IT team configured to hold it.' }
                ]
            },
            {
                subtitle: 'Sender Details',
                fields: [
                    { key: 'Sms.Msg91.SenderId', label: 'Sender ID', type: 'text', help: 'The short code/name recipients see as the sender.' },
                    { key: 'Sms.Country', label: 'Country', type: 'text' }
                ]
            },
            {
                subtitle: 'DLT Configuration',
                note: 'Required by Indian telecom regulations (TRAI) when using MSG91.',
                fields: [
                    { key: 'Sms.Msg91.DltEntityId', label: 'DLT Entity ID', type: 'text', full: true, requiredIfProviderKey: 'Sms.Provider', requiredIfProviderValue: 'MSG91' },
                    { key: 'Sms.Msg91.TemplateId', label: 'Message Template ID', type: 'text', full: true },
                    { key: 'Sms.Msg91.Route', label: 'Delivery Route', type: 'select', options: ['Transactional', 'Promotional'] }
                ]
            }
        ]
    },
    {
        key: 'service',
        icon: '⚙️',
        title: 'Communication Service',
        description: 'Monitor and control how queued communications are processed in the background.',
        sections: [
            {
                subtitle: 'Service Settings',
                fields: [
                    { key: 'General.QueueProcessingEnabled', label: 'Communication Worker Enabled', type: 'switch', help: 'Turn off to pause all background email/SMS delivery without restarting the portal.' },
                    { key: 'General.WorkerIntervalSeconds', label: 'Queue Check Interval (seconds)', type: 'number', help: 'How often the background worker checks for new messages to send.' },
                    { key: 'General.MaxRetryCount', label: 'Retry Attempts', type: 'number', help: 'How many times a failed message is automatically retried before it is marked Failed.' }
                ]
            }
        ]
    }
];

// Sprint 2.7.2B (Enterprise Communication Experience Final Polish) — Parts 1/2/4: Advanced Settings used
// to render its 7 remaining Communication ConfigKeys as a raw key/value table (developer-oriented key
// names, plain-text "true"/"false" values). Every entry below is exactly one of those same 7 ConfigKeys,
// nothing added or removed — just given the same friendly label/help/toggle treatment as every other
// Communication field, via the existing renderFriendlyField (no new rendering logic needed). Boolean
// "Enabled" flags use type 'switch' instead of a text box holding the word "true"/"false" (Part 4).
// (LastTestResult/LastTestAt are deliberately not in this list — they are system-managed, set only by Test
// Email/Test SMS, and already shown read-only as part of the Testing section further down this file.)
const COMMUNICATION_ADVANCED_FIELDS = [
    { key: 'Email.SendGrid.Enabled', label: 'Enable SendGrid Provider', type: 'switch', help: 'Turn on to allow SendGrid to send email when it is selected as the Email Service Provider above.' },
    { key: 'Email.AzureCommunicationServices.Enabled', label: 'Enable Azure Communication Services (Email)', type: 'switch', help: 'Reserved for a future email delivery option. Not yet active in this environment.' },
    { key: 'Email.AmazonSES.Enabled', label: 'Enable Amazon SES Provider', type: 'switch', help: 'Reserved for a future email delivery option. Not yet active in this environment.' },
    { key: 'Sms.AzureCommunicationServices.Enabled', label: 'Enable Azure Communication Services (SMS)', type: 'switch', help: 'Reserved for a future SMS delivery option. Not yet active in this environment.' },
    { key: 'Sms.Twilio.Enabled', label: 'Enable Twilio Provider', type: 'switch', help: 'Reserved for a future SMS delivery option. Not yet active in this environment.' },
    { key: 'General.MaxQueueBatchSize', label: 'Maximum Messages Processed Per Cycle', type: 'number', help: 'The largest number of queued messages the background service will send in a single processing cycle.' },
    { key: 'General.RetryPolicy', label: 'Retry Policy', type: 'text', help: 'The strategy used when automatically retrying a failed message. Leave as provided unless your technical team advises a change.' }
];

// OTP Configuration section reuses the same 2 mechanisms/feature-flags the Identity tab already shows —
// an administrator configuring "communication" naturally expects to see OTP delivery here too. Same
// underlying data/handlers as IDENTITY_MECHANISMS below; not a second source of truth.
const OTP_CHANNELS_FOR_COMMUNICATION_TAB = [
    { key: 'EmailOtp', label: 'Email OTP', flagKey: 'EmailOtpVerification', healthComponent: 'Email (SendGrid)', providerNote: 'Delivered via your Email Service Provider setting above.' },
    { key: 'MobileOtp', label: 'SMS OTP', flagKey: 'MobileOtpVerification', healthComponent: 'Mobile OTP (MSG91)', providerNote: 'Always delivered via MSG91, regardless of the SMS Service Provider setting above.' }
];

const { TabPane } = Tabs;

// Sprint 2.4 (Enterprise Configuration Experience): tabs are generated from whatever categories the
// backend actually returns (grouped client-side), rather than a hardcoded list — Sprint 2.3 seeded
// FeatureFlag/RateLimiting/SessionTimeout/PasswordPolicy/BackupAwareness/Branding; any future category
// added on the backend appears here automatically with no frontend change required.
class EnterpriseConfiguration extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            items: [],
            savingId: null,
            healthStatus: [],
            communicationHealth: null,
            testEmailAddress: '',
            testMobileNumber: '',
            testingEmail: false,
            testingSms: false,
            queueSummary: null,
            loadingQueueSummary: false,
            advancedExpanded: false,
            fieldErrors: {},
            savingGroupKey: null
        };
    }

    componentDidMount() {
        this.loadConfig();
        this.loadHealthStatus();
        this.loadCommunicationHealth();
        this.loadQueueSummary();
    }

    loadConfig = async () => {
        this.setState({ loading: true });
        try {
            const response = await _EnterpriseOpsService.getAllConfig();
            this.setState({ items: response.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
        }
    };

    // Sprint 2.6.5: reuses the existing System Health endpoint (Sprint 2.3, already surfaced in
    // ComplianceCenter.jsx) to answer "is this mechanism's vendor actually configured" — no new backend
    // service, per the sprint's explicit instruction.
    loadHealthStatus = async () => {
        try {
            const response = await _EnterpriseOpsService.getHealthStatus();
            this.setState({ healthStatus: response.data || [] });
        } catch (e) {
            // Non-fatal — the Identity tab still shows Enabled/Disabled from feature flags even if the
            // health endpoint is unreachable; only the "Configured/Not Configured" half is affected.
        }
    };

    // Sprint 2.7.1 (Enterprise Communication Foundation) — Part 4: purely config-derived, no polling —
    // re-fetched once on mount and again immediately after a Test Email/Test SMS action (Part 5), since
    // that action changes the Last Test Result this same endpoint reports.
    loadCommunicationHealth = async () => {
        try {
            const response = await _EnterpriseOpsService.getCommunicationHealth();
            this.setState({ communicationHealth: response.data || null });
        } catch (e) {
            // Non-fatal — the tab still renders with "Not Tested"/empty state if this call fails.
        }
    };

    // Sprint 2.7.2 (Enterprise Communication Engine) — Part 10: manual refresh only, no polling — this is
    // an operational status snapshot, not a live dashboard, per the sprint's explicit "do not build
    // analytics/dashboards" boundary.
    loadQueueSummary = async () => {
        this.setState({ loadingQueueSummary: true });
        try {
            const response = await _EnterpriseOpsService.getQueueSummary();
            this.setState({ queueSummary: response.data || null, loadingQueueSummary: false });
        } catch (e) {
            this.setState({ loadingQueueSummary: false });
        }
    };

    handleTestEmail = async () => {
        const { testEmailAddress } = this.state;
        if (!testEmailAddress || !testEmailAddress.trim()) {
            _notification.showMessage('error', 'Enter a recipient email address first.');
            return;
        }
        this.setState({ testingEmail: true });
        try {
            const response = await _EnterpriseOpsService.testEmail(testEmailAddress.trim());
            const result = response.data || {};
            _notification.showMessage(result.success ? 'success' : 'error', result.message || (result.success ? 'Test email sent.' : 'Test email failed.'));
        } catch (e) {
            _notification.showMessage('error', 'Something went wrong sending the test email.');
        }
        this.setState({ testingEmail: false });
        this.loadCommunicationHealth();
    };

    handleTestSms = async () => {
        const { testMobileNumber } = this.state;
        if (!testMobileNumber || !testMobileNumber.trim()) {
            _notification.showMessage('error', 'Enter a recipient mobile number first.');
            return;
        }
        this.setState({ testingSms: true });
        try {
            const response = await _EnterpriseOpsService.testSms(testMobileNumber.trim());
            const result = response.data || {};
            _notification.showMessage(result.success ? 'success' : 'error', result.message || (result.success ? 'Test SMS sent.' : 'Test SMS failed.'));
        } catch (e) {
            _notification.showMessage('error', 'Something went wrong sending the test SMS.');
        }
        this.setState({ testingSms: false });
        this.loadCommunicationHealth();
    };

    // Sprint 2.7.2A (Enterprise Communication Experience Modernization) — helpers for the friendly
    // Communication form. All read/write through the exact same `items`/`handleValueChange`/`saveItem`
    // mechanism the raw grid already used — no new API call, no new data shape.
    getCommItem = (configKey) => this.state.items.find(i => i.Category === 'Communication' && i.ConfigKey === configKey);
    getCommValue = (configKey) => {
        const item = this.getCommItem(configKey);
        return item ? (item.ConfigValue || '') : '';
    };

    validateField = (field, value) => {
        if (field.type === 'number' && value && !/^\d+$/.test(value)) {
            return field.label + ' must be numeric.';
        }
        if (field.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return field.label + ' must be a valid email address.';
        }
        if (!field.optional && field.type !== 'switch' && !value) {
            return field.label + ' is required.';
        }
        if (field.requiredIfProviderKey && this.getCommValue(field.requiredIfProviderKey) === field.requiredIfProviderValue && !value) {
            return field.label + ' cannot be blank when ' + field.requiredIfProviderValue + ' is selected.';
        }
        return null;
    };

    handleFriendlyFieldChange = (field, rawValue) => {
        const item = this.getCommItem(field.key);
        if (!item) return;
        const error = this.validateField(field, rawValue);
        this.setState(prevState => ({
            items: prevState.items.map(i => i.Id === item.Id ? { ...i, ConfigValue: rawValue } : i),
            fieldErrors: { ...prevState.fieldErrors, [field.key]: error }
        }));
    };

    // One "Save" per section (per the brief's suggested layout) instead of one Save per field — saves
    // every field belonging to that group together, via the same updateConfig endpoint each field already
    // used individually.
    handleSaveGroup = async (group) => {
        const allFields = group.sections.reduce((acc, s) => acc.concat(s.fields), []);
        const errors = {};
        let hasError = false;
        allFields.forEach(field => {
            const err = this.validateField(field, this.getCommValue(field.key));
            if (err) { errors[field.key] = err; hasError = true; }
        });
        this.setState(prevState => ({ fieldErrors: { ...prevState.fieldErrors, ...errors } }));
        if (hasError) {
            _notification.showMessage('error', 'Please fix the highlighted fields before saving.');
            return;
        }

        this.setState({ savingGroupKey: group.key });
        try {
            for (const field of allFields) {
                const item = this.getCommItem(field.key);
                if (!item) continue;
                // eslint-disable-next-line no-await-in-loop
                await _EnterpriseOpsService.updateConfig({ Id: item.Id, ConfigValue: item.ConfigValue, IsActive: item.IsActive });
            }
            _notification.showMessage('success', group.title + ' settings saved.');
        } catch (e) {
            _notification.showMessage('error', 'Something went wrong saving ' + group.title + '.');
        }
        this.setState({ savingGroupKey: null });
    };

    handleValueChange = (id, value) => {
        this.setState(prevState => ({
            items: prevState.items.map(item => item.Id === id ? { ...item, ConfigValue: value } : item)
        }));
    };

    handleActiveToggle = (id, isActive) => {
        this.setState(prevState => ({
            items: prevState.items.map(item => item.Id === id ? { ...item, IsActive: isActive } : item)
        }), () => this.saveItem(id));
    };

    handleFeatureFlagToggle = (id, enabled) => {
        this.setState(prevState => ({
            items: prevState.items.map(item => item.Id === id ? { ...item, ConfigValue: enabled ? 'true' : 'false' } : item)
        }), () => this.saveItem(id));
    };

    saveItem = async (id) => {
        const item = this.state.items.find(i => i.Id === id);
        if (!item) return;

        this.setState({ savingId: id });
        try {
            const response = await _EnterpriseOpsService.updateConfig({
                Id: item.Id,
                ConfigValue: item.ConfigValue,
                IsActive: item.IsActive
            });
            if (response.data && response.data.IsSuccess) {
                _notification.showMessage('success', response.data.Message || 'Updated successfully.');
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || 'Update failed.');
            }
        } catch (e) {
            _notification.showMessage('error', 'Something went wrong.');
        }
        this.setState({ savingId: null });
    };

    renderFeatureFlagRow = (item) => (
        <div key={item.Id} className="ent-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }}>
            <div>
                <div style={{ fontWeight: 600 }}>{item.ConfigKey}</div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral)' }}>{item.Description}</div>
            </div>
            <Switch
                checked={item.ConfigValue === 'true' || item.ConfigValue === true}
                loading={this.state.savingId === item.Id}
                onChange={(checked) => this.handleFeatureFlagToggle(item.Id, checked)}
            />
        </div>
    );

    renderGenericRow = (item) => (
        <tr key={item.Id}>
            <td>{item.ConfigKey}</td>
            <td style={{ maxWidth: 260 }}>
                <MyInput
                    name={'cfg_' + item.Id}
                    type="text"
                    value={item.ConfigValue || ''}
                    onChange={(e) => this.handleValueChange(item.Id, e.target.value)}
                />
            </td>
            <td>{item.Description}</td>
            <td>
                <Switch
                    size="small"
                    checked={!!item.IsActive}
                    onChange={(checked) => this.handleActiveToggle(item.Id, checked)}
                />
            </td>
            <td>
                <Button
                    type="button"
                    value={this.state.savingId === item.Id ? 'Saving...' : 'Save'}
                    disabled={this.state.savingId === item.Id}
                    className="button-global button-primary autoWidth"
                    onClick={() => this.saveItem(item.Id)}
                />
            </td>
        </tr>
    );

    // Sprint 2.6.3 (Enterprise Operations Console): curated sections replacing "one tab per raw DB
    // category". The master brief's suggested Branding/Payments/Notifications/Identity/Localization
    // sections were deliberately NOT added as empty placeholder tabs at that time — see
    // S2_6_3_Admin_Experience_Register.md for the full reasoning.
    // CORRECTION (Sprint 2.6.5): this comment previously claimed the Identity2026 seed script had real
    // backing rows in every environment — Phase A discovery of Sprint 2.6.5 found that was false for at
    // least one environment (MobileOtpVerification/EmailOtpVerification rows and the entire
    // IdentityVerificationSession table were missing there; both were applied as part of that discovery —
    // see S2_6_5_Identity_Discovery_Assessment.md). An "Identity" tab now exists (see
    // IDENTITY_MECHANISMS/renderIdentityTab below) backed by real FeatureFlag rows plus a new
    // IdentityPolicy category — do not assume backing data exists in any environment without checking.
    renderGenericTable = (categoryList) => (
        <div className="ent-section">
            <table className="ent-table">
                <thead>
                    <tr>
                        <th>Key</th>
                        <th>Value</th>
                        <th>Description</th>
                        <th>Active</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {categoryList.map(this.renderGenericRow)}
                </tbody>
            </table>
        </div>
    );

    // Sprint 2.6.5: combines a mechanism's EnterpriseConfiguration feature-flag state (Enabled/Disabled)
    // with SystemHealthService's vendor config-presence state (Configured/Not Configured) into the single
    // indicator Part 1 of the sprint brief asks for, per mechanism.
    renderIdentityMechanism = (mechanism) => {
        const flagItem = mechanism.flagKey ? this.state.items.find(i => i.Category === 'FeatureFlag' && i.ConfigKey === mechanism.flagKey) : null;
        const healthItem = mechanism.healthComponent ? this.state.healthStatus.find(h => h.Component === mechanism.healthComponent) : null;

        let status = 'Future Capability';
        let tone = 'neutral';
        if (mechanism.isFuture) {
            status = 'Future Capability';
            tone = 'neutral';
        } else if (!flagItem) {
            status = 'Not Configured';
            tone = 'neutral';
        } else {
            const enabled = flagItem.ConfigValue === 'true' || flagItem.ConfigValue === true;
            const vendorConfigured = healthItem ? healthItem.Status !== 'NotConfigured' : null;
            if (!enabled) {
                status = 'Disabled';
                tone = 'neutral';
            } else if (vendorConfigured === false) {
                status = 'Enabled — Not Configured';
                tone = 'warning';
            } else {
                status = 'Enabled';
                tone = 'success';
            }
        }

        return (
            <div key={mechanism.key} className="ent-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }}>
                <div>
                    <div style={{ fontWeight: 600 }}>{mechanism.label}</div>
                    {healthItem && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral)' }}>{healthItem.Detail}</div>}
                    {!healthItem && flagItem && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral)' }}>{flagItem.Description}</div>}
                    {mechanism.isFuture && !flagItem && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral)' }}>Not yet implemented — reserved for a future sprint.</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                    <StatusBadge status={status} tone={tone} />
                    {flagItem && !mechanism.isFuture && (
                        <Switch
                            checked={flagItem.ConfigValue === 'true' || flagItem.ConfigValue === true}
                            loading={this.state.savingId === flagItem.Id}
                            onChange={(checked) => this.handleFeatureFlagToggle(flagItem.Id, checked)}
                        />
                    )}
                </div>
            </div>
        );
    };

    renderIdentityTab = (policyItems) => (
        <div>
            {IDENTITY_MECHANISMS.map(this.renderIdentityMechanism)}
            {policyItems.length > 0 && (
                <div style={{ marginTop: 'var(--spacing-lg)' }}>
                    <div className="ent-panel-title">OTP Policy (informational — actual value is read from Web.config/environment variables, not this row)</div>
                    {this.renderGenericTable(policyItems)}
                </div>
            )}
        </div>
    );

    // Sprint 2.7.2A (Enterprise Communication Experience Modernization): renders one administrator-
    // friendly field, resolved from COMMUNICATION_FIELD_GROUPS — the one place a ConfigKey turns into a
    // label/input a non-technical administrator can read. Long values (secret references) always get a
    // full-width input, per the sprint's "no narrow grid cells for large values" requirement.
    renderFriendlyField = (field) => {
        const item = this.getCommItem(field.key);
        if (!item) return null; // e.g. seed script not yet applied in this environment — fail quietly, not with a crash
        const value = item.ConfigValue || '';
        const error = this.state.fieldErrors[field.key];
        const wrapClass = 'ent-comm-field' + (field.full ? ' ent-comm-field-full' : '');
        const heading = <div className='displayInGrid'><div>{field.label}{!field.optional && field.type !== 'switch' ? <span className="color-red">*</span> : ''}</div></div>;

        if (field.type === 'switch') {
            return (
                <div key={field.key} className={wrapClass} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 600 }}>{field.label}</div>
                        {field.help && <div className="ent-field-help">{field.help}</div>}
                    </div>
                    <Switch checked={value === 'true' || value === true} onChange={(checked) => this.handleFriendlyFieldChange(field, checked ? 'true' : 'false')} />
                </div>
            );
        }

        if (field.type === 'select') {
            return (
                <div key={field.key} className={wrapClass}>
                    <label className="card-holder-name">{heading}</label>
                    <select className={'form-control' + (error ? ' has-error' : '')} value={value} onChange={(e) => this.handleFriendlyFieldChange(field, e.target.value)}>
                        {field.options.map(opt => <option key={opt} value={opt}>{(field.optionLabels && field.optionLabels[opt]) || opt}</option>)}
                    </select>
                    {error && <label className="color-red d-block">{error}</label>}
                    {field.help && !error && <div className="ent-field-help">{field.help}</div>}
                </div>
            );
        }

        if (field.type === 'secretRef') {
            return (
                <div key={field.key} className={wrapClass}>
                    <SecretRefField
                        name={'comm_' + field.key}
                        value={value}
                        onChange={(e) => this.handleFriendlyFieldChange(field, e.target.value)}
                        heading={heading}
                        helpText={field.help}
                        isRequired={!field.optional}
                        errorState={error}
                        errorMessage={error}
                        className="w-100"
                    />
                </div>
            );
        }

        return (
            <div key={field.key} className={wrapClass}>
                <MyInput
                    name={'comm_' + field.key}
                    type={field.type === 'number' ? 'text' : 'text'}
                    value={value}
                    onChange={(e) => this.handleFriendlyFieldChange(field, e.target.value)}
                    heading={heading}
                    isRequired={!field.optional}
                    errorState={error}
                    errorMessage={error}
                    className="w-100"
                />
                {field.help && !error && <div className="ent-field-help">{field.help}</div>}
            </div>
        );
    };

    // One top-level group (Email Communication / SMS Communication / Communication Service) — icon,
    // title, plain-language description, its subsections, and one Save button at the end that is always
    // visible without scrolling past the group's own fields.
    renderCommunicationGroup = (group) => (
        <div key={group.key} className="ent-section">
            <div className="ent-comm-section-header">
                <span className="ent-comm-section-icon" aria-hidden="true">{group.icon}</span>
                <div>
                    <div className="ent-section-title" style={{ marginBottom: 0 }}>{group.title}</div>
                    <div className="ent-comm-section-desc">{group.description}</div>
                </div>
            </div>
            {group.sections.map((section, idx) => (
                <div key={idx}>
                    <div className="ent-comm-subsection-title">{section.subtitle}</div>
                    {section.note && <div className="ent-field-help" style={{ marginBottom: 'var(--spacing-sm)' }}>{section.note}</div>}
                    <div className="ent-comm-field-grid">
                        {section.fields.map(this.renderFriendlyField)}
                    </div>
                </div>
            ))}
            {group.key === 'service' && this.renderQueueSummary()}
            <div className="ent-section-save-row">
                <Button
                    type="button"
                    value={this.state.savingGroupKey === group.key ? 'Saving...' : 'Save'}
                    disabled={this.state.savingGroupKey === group.key}
                    className="button-global button-primary autoWidth"
                    onClick={() => this.handleSaveGroup(group)}
                />
            </div>
        </div>
    );

    // OTP Configuration — reuses the exact same feature-flag toggle/health data the Identity tab shows,
    // presented in the Communication experience too since an administrator configuring "communication"
    // naturally expects OTP delivery to be part of it.
    renderOtpSection = () => (
        <div className="ent-section">
            <div className="ent-comm-section-header">
                <span className="ent-comm-section-icon" aria-hidden="true">🔐</span>
                <div>
                    <div className="ent-section-title" style={{ marginBottom: 0 }}>OTP Configuration</div>
                    <div className="ent-comm-section-desc">Control whether candidates must verify their email and/or mobile number with a one-time code during registration.</div>
                </div>
            </div>
            {OTP_CHANNELS_FOR_COMMUNICATION_TAB.map(channel => {
                const flagItem = this.state.items.find(i => i.Category === 'FeatureFlag' && i.ConfigKey === channel.flagKey);
                const healthItem = this.state.healthStatus.find(h => h.Component === channel.healthComponent);
                const enabled = flagItem ? (flagItem.ConfigValue === 'true' || flagItem.ConfigValue === true) : false;
                const configured = healthItem ? healthItem.Status !== 'NotConfigured' : null;
                let status = 'Disabled', tone = 'neutral';
                if (enabled) {
                    status = configured === false ? 'Enabled — Not Configured' : 'Enabled';
                    tone = configured === false ? 'warning' : 'success';
                }
                return (
                    <div key={channel.key} className="ent-comm-field" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }}>
                        <div>
                            <div style={{ fontWeight: 600 }}>{channel.label}</div>
                            <div className="ent-field-help">{channel.providerNote}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                            <StatusBadge status={status} tone={tone} />
                            {flagItem && (
                                <Switch
                                    checked={enabled}
                                    loading={this.state.savingId === flagItem.Id}
                                    onChange={(checked) => this.handleFeatureFlagToggle(flagItem.Id, checked)}
                                />
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    // Communication Testing — the exact same Test Email/Test SMS logic already built (Sprint 2.7.1/2.7.2),
    // restyled into its own clearly-labeled section rather than being folded into the channel status rows.
    renderTestingSection = () => (
        <div className="ent-section">
            <div className="ent-comm-section-header">
                <span className="ent-comm-section-icon" aria-hidden="true">🧪</span>
                <div>
                    <div className="ent-section-title" style={{ marginBottom: 0 }}>Communication Testing</div>
                    <div className="ent-comm-section-desc">Send a real test message to confirm your Email and SMS settings are working.</div>
                </div>
            </div>
            {COMMUNICATION_CHANNELS.map(this.renderCommunicationChannel)}
        </div>
    );

    // Advanced Settings — every remaining Communication ConfigKey, collapsed by default. Nothing here is
    // removed or hidden permanently — it is one click away, per "do not remove configuration simply
    // because it appears technical." Sprint 2.7.2B: switched from the raw key/value table to the same
    // friendly field grid + single Save button every other Communication section already uses (Parts
    // 1/2/3/4) — same underlying ConfigKeys, same save mechanism (handleSaveGroup), just readable.
    renderAdvancedSection = () => {
        const advancedGroup = { key: 'advanced', title: 'Advanced Settings', sections: [{ fields: COMMUNICATION_ADVANCED_FIELDS }] };
        return (
            <div className="ent-section">
                <button
                    type="button"
                    className="ent-advanced-toggle"
                    onClick={() => this.setState(prev => ({ advancedExpanded: !prev.advancedExpanded }))}
                    aria-expanded={this.state.advancedExpanded}
                >
                    <span>▼ Advanced Settings</span>
                    <span>{this.state.advancedExpanded ? 'Hide' : 'Show'}</span>
                </button>
                {this.state.advancedExpanded && (
                    <div style={{ marginTop: 'var(--spacing-md)' }}>
                        <div className="ent-field-help" style={{ marginBottom: 'var(--spacing-sm)' }}>
                            These settings are reserved for future communication providers and internal service tuning.
                            Most administrators will not need to change them.
                        </div>
                        <div className="ent-comm-field-grid">
                            {COMMUNICATION_ADVANCED_FIELDS.map(this.renderFriendlyField)}
                        </div>
                        <div className="ent-section-save-row">
                            <Button
                                type="button"
                                value={this.state.savingGroupKey === 'advanced' ? 'Saving...' : 'Save'}
                                disabled={this.state.savingGroupKey === 'advanced'}
                                className="button-global button-primary autoWidth"
                                onClick={() => this.handleSaveGroup(advancedGroup)}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Sprint 2.7.1 (Enterprise Communication Foundation) — Parts 4/5: per-channel status (mirrors
    // renderIdentityMechanism's Enabled/Disabled/Configured pattern) plus a Send Test Email/SMS action.
    // GetHealth is framework-only (config-derived); the test send is the one action that actually
    // contacts a vendor, per the sprint's explicit "no runtime monitoring" boundary.
    renderCommunicationChannel = (channel) => {
        const health = this.state.communicationHealth ? this.state.communicationHealth[channel.healthKey] : null;
        const configTone = health && health.ConfigurationComplete ? 'success' : 'warning';
        const configStatus = health ? health.ConfigurationStatus : 'Unknown';
        const testResult = health ? health.LastTestResult : 'Not Tested';
        const testTone = testResult === 'Success' ? 'success' : testResult === 'Failed' ? 'error' : 'neutral';
        const testValue = channel.key === 'Email' ? this.state.testEmailAddress : this.state.testMobileNumber;
        const testing = channel.key === 'Email' ? this.state.testingEmail : this.state.testingSms;
        const onTestValueChange = (e) => this.setState(channel.key === 'Email' ? { testEmailAddress: e.target.value } : { testMobileNumber: e.target.value });
        const onSendTest = channel.key === 'Email' ? this.handleTestEmail : this.handleTestSms;

        return (
            <div key={channel.key} className="ent-section" style={{ marginBottom: 'var(--spacing-md)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 600 }}>{channel.label}</div>
                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral)' }}>
                            Provider: {health ? health.ActiveProvider : '—'}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                        <StatusBadge status={configStatus} tone={configTone} />
                        <StatusBadge status={'Last test: ' + testResult} tone={testTone} />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-sm)', alignItems: 'center' }}>
                    <MyInput
                        name={'test_' + channel.key}
                        type="text"
                        value={testValue || ''}
                        placeholder={channel.testPlaceholder}
                        onChange={onTestValueChange}
                        wrapClass="mb0"
                        className="w-100"
                    />
                    <Button
                        type="button"
                        value={testing ? 'Sending...' : channel.testLabel}
                        disabled={testing}
                        className="button-global button-primary autoWidth"
                        onClick={onSendTest}
                    />
                </div>
                {health && health.LastTestAtUtc && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral)', marginTop: '4px' }}>
                        Last tested: {new Date(health.LastTestAtUtc).toLocaleString()} (UTC)
                    </div>
                )}
            </div>
        );
    };

    // Sprint 2.7.2 (Enterprise Communication Engine) — Part 10: Pending/Processing/Failed/Completed
    // counts only. A plain StatCard row, matching the design language already used on Dashboard.jsx —
    // deliberately not a chart/graph, per the sprint's own "operational status page only" instruction.
    renderQueueSummary = () => {
        const { queueSummary, loadingQueueSummary } = this.state;
        return (
            <div className="ent-section" style={{ marginTop: 'var(--spacing-lg)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }}>
                    <div className="ent-panel-title">Communication Queue Status</div>
                    <Button
                        type="button"
                        value={loadingQueueSummary ? 'Refreshing...' : 'Refresh'}
                        disabled={loadingQueueSummary}
                        className="button-global button-border autoWidth"
                        onClick={this.loadQueueSummary}
                    />
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
                    <StatCard label="Pending" value={queueSummary ? queueSummary.Pending : '—'} />
                    <StatCard label="Processing" value={queueSummary ? queueSummary.Processing : '—'} />
                    <StatCard label="Failed" value={queueSummary ? queueSummary.Failed : '—'} tone={(queueSummary && queueSummary.Failed > 0) ? 'warning' : undefined} />
                    <StatCard label="Completed" value={queueSummary ? queueSummary.Completed : '—'} tone="success" />
                </div>
            </div>
        );
    };

    // Sprint 2.7.2A (Enterprise Communication Experience Modernization): the full administrator
    // experience — Email, SMS, OTP, Communication Service (with live queue status), Testing, then
    // Advanced Settings collapsed at the very end. Replaces the developer-oriented raw grid entirely for
    // this tab; every capability that grid exposed is still here (see ECMF_Information_Architecture.md's
    // completeness map), just organized around what an administrator is trying to do.
    renderCommunicationTab = () => (
        <div>
            {COMMUNICATION_FIELD_GROUPS.filter(g => g.key !== 'service').map(this.renderCommunicationGroup)}
            {this.renderOtpSection()}
            {COMMUNICATION_FIELD_GROUPS.filter(g => g.key === 'service').map(this.renderCommunicationGroup)}
            {this.renderTestingSection()}
            {this.renderAdvancedSection()}
        </div>
    );

    render() {
        const { loading, items } = this.state;

        const featureFlagItems = items.filter(i => i.Category === 'FeatureFlag');
        const securityItems = items.filter(i => ['RateLimiting', 'SessionTimeout', 'PasswordPolicy'].indexOf(i.Category) !== -1);
        const operationsItems = items.filter(i => i.Category === 'BackupAwareness');
        const identityPolicyItems = items.filter(i => i.Category === 'IdentityPolicy');
        // Anything seeded under a category not yet mapped to one of the sections above still shows,
        // under its raw category name, rather than being silently hidden.
        const knownCategories = ['FeatureFlag', 'RateLimiting', 'SessionTimeout', 'PasswordPolicy', 'BackupAwareness', 'IdentityPolicy', 'Communication'];
        const otherCategories = [...new Set(items.filter(i => knownCategories.indexOf(i.Category) === -1).map(i => i.Category))];

        return (
            <div className="ent-admin-page">
                <PageHeader
                    title="Enterprise Configuration"
                    subtitle="Manage feature flags, password policy, rate limiting, and other platform-wide settings without a deployment."
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Enterprise Configuration' }]}
                />

                {loading ? <LoadingSpinner tip="Loading configuration..." /> : (
                    items.length === 0 ? (
                        <EmptyState icon="setting" title="No configuration found" description="No EnterpriseConfiguration rows exist yet." />
                    ) : (
                        <Tabs defaultActiveKey="featureFlags">
                            <TabPane tab="Feature Flags" key="featureFlags">
                                {featureFlagItems.map(this.renderFeatureFlagRow)}
                            </TabPane>
                            <TabPane tab="Identity" key="identity">
                                {this.renderIdentityTab(identityPolicyItems)}
                            </TabPane>
                            <TabPane tab="Communication" key="communication">
                                {this.renderCommunicationTab()}
                            </TabPane>
                            <TabPane tab="Security" key="security">
                                {this.renderGenericTable(securityItems)}
                            </TabPane>
                            <TabPane tab="Operations" key="operations">
                                {this.renderGenericTable(operationsItems)}
                            </TabPane>
                            {otherCategories.map(category => (
                                <TabPane tab={category} key={category}>
                                    {this.renderGenericTable(items.filter(i => i.Category === category))}
                                </TabPane>
                            ))}
                        </Tabs>
                    )
                )}
            </div>
        );
    }
}

export default EnterpriseConfiguration;
